import * as Sentry from '@sentry/react'
import { useTree } from '@headless-tree/react'
import { useRouter } from '@tanstack/react-router'
import { Button, Input, cn } from '@repo/ui'
import { AlertCircle, CheckCircle2, ChevronDown, Clock, FileIcon, FolderIcon, FolderOpen, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChildrenWithData, TreeItem, WebCrawlerItem } from '@/api/content/connector'
import {
  ConnectorType,
  FileSyncState,
  connectorGetChildrenWithData,
  connectorGetItem,
  getConnectorCurrentConfig,
  getWebCrawlerSubUrls,
  saveConnectorConfig
} from '@/api/content/connector'
import {
  TREE_ROOT_NAME,
  getTreeConfig,
  getTreeStateCheckedAndIndeterminateItems,
  staticDataAsyncDataLoader
} from './connectorFileTreeSource'
import { processingDetailsDisplay } from './processingDetails'
import type { TreeDataLoader } from '@headless-tree/core'
import type { TreeNodes } from './connectorFileTreeSource'

function PanelLoading() {
  return (
    <div className="flex justify-center rounded-md border bg-muted/30 py-8">
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
    </div>
  )
}

function PanelError({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-md border bg-muted/30 py-4 text-center text-sm text-destructive">
      {message}
    </div>
  )
}

interface ConnectorFileTreeProps {
  projectId?: number
  connectorId: number
  connectorType: string
  editMode: boolean
  onCancelEdit: () => void
  onAuthorityChanged?: () => void | Promise<void>
}

export function ConnectorFileTree({
  projectId,
  connectorId,
  connectorType,
  editMode,
  onCancelEdit,
  onAuthorityChanged
}: ConnectorFileTreeProps) {
  const [config, setConfig] = useState<{
    fileConfig: Array<TreeItem> | null
    webCrawlerConfig: WebCrawlerItem | null
    webCrawlerSubUrls: Array<string>
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  function handleSaveComplete() {
    setLoading(true)
    setConfig(null)
    setError(null)
    setRefreshKey((k) => k + 1)
  }

  useEffect(() => {
    let cancelled = false
    const configPromise = getConnectorCurrentConfig(connectorId, projectId)
    const subUrlsPromise =
      connectorType === ConnectorType.WebCrawler
        ? getWebCrawlerSubUrls(connectorId, projectId).catch((err) => {
            Sentry.captureException(err, { extra: { connectorId, projectId } })
            return [] as Array<string>
          })
        : Promise.resolve([] as Array<string>)
    Promise.all([configPromise, subUrlsPromise])
      .then(([result, subUrls]) => {
        if (!cancelled) {
          setConfig({ ...result, webCrawlerSubUrls: subUrls })
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          Sentry.captureException(err, { extra: { connectorId, projectId } })
          setError(err instanceof Error ? err.message : 'Failed to load config')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [connectorId, connectorType, projectId, refreshKey])

  if (loading) return <PanelLoading />
  if (error) return <PanelError message={error} />
  if (!config) return null

  if (connectorType === ConnectorType.WebCrawler) {
    return (
      <WebCrawlerConfig
        currentConfig={config.webCrawlerConfig ?? { url: '' }}
        connectorId={connectorId}
        {...(projectId !== undefined ? { projectId } : {})}
        editMode={editMode}
        onCancelEdit={onCancelEdit}
        onSaveComplete={handleSaveComplete}
        {...(onAuthorityChanged !== undefined ? { onAuthorityChanged } : {})}
        subUrls={config.webCrawlerSubUrls}
      />
    )
  }

  if (config.fileConfig) {
    const treeNodes: TreeNodes = {}
    for (const item of config.fileConfig) {
      treeNodes[item.id] = item
    }
    return (
      <FileTreeGetItems
        currentTreeConfig={treeNodes}
        {...(projectId !== undefined ? { projectId } : {})}
        connectorId={connectorId}
        editMode={editMode}
        onCancelEdit={onCancelEdit}
        onSaveComplete={handleSaveComplete}
        {...(onAuthorityChanged !== undefined ? { onAuthorityChanged } : {})}
      />
    )
  }

  return (
    <div className="rounded-md border bg-muted/30 py-4 text-center text-sm text-muted-foreground">
      No configuration available.
    </div>
  )
}

function FileTreeGetItems({
  currentTreeConfig,
  projectId,
  connectorId,
  editMode,
  onCancelEdit,
  onSaveComplete,
  onAuthorityChanged
}: {
  currentTreeConfig: TreeNodes
  projectId?: number
  connectorId: number
  editMode: boolean
  onCancelEdit: () => void
  onSaveComplete: () => void
  onAuthorityChanged?: () => void | Promise<void>
}) {
  const [rootItem, setRootItem] = useState<TreeItem | null>(null)
  const [rootChildren, setRootChildren] = useState<ChildrenWithData | null>(null)
  const [loading, setLoading] = useState(editMode)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editMode) return
    let cancelled = false
    Promise.all([
      connectorGetItem(connectorId, TREE_ROOT_NAME, projectId),
      connectorGetChildrenWithData(connectorId, TREE_ROOT_NAME, projectId)
    ])
      .then(([item, children]) => {
        if (!cancelled) {
          setRootItem(item)
          setRootChildren(children)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          Sentry.captureException(err, { extra: { connectorId, projectId } })
          setError(err instanceof Error ? err.message : 'Failed to load tree')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [editMode, connectorId, projectId])

  if (error) return <PanelError message={error} />
  if (editMode && (loading || !rootItem || !rootChildren)) {
    return <PanelLoading />
  }

  let asyncDataLoader: TreeDataLoader<TreeItem>
  if (editMode) {
    asyncDataLoader = {
      getItem: (itemId: string) => connectorGetItem(connectorId, itemId, projectId),
      getChildrenWithData: (itemId: string) => connectorGetChildrenWithData(connectorId, itemId, projectId)
    }
  } else {
    asyncDataLoader = staticDataAsyncDataLoader(currentTreeConfig)
  }

  return (
    <FileTreeWithDataLoader
      key={editMode ? 'edit' : 'view'}
      {...(projectId !== undefined ? { projectId } : {})}
      connectorId={connectorId}
      asyncDataLoader={asyncDataLoader}
      editMode={editMode}
      onCancelEdit={onCancelEdit}
      onSaveComplete={onSaveComplete}
      {...(onAuthorityChanged !== undefined ? { onAuthorityChanged } : {})}
    />
  )
}

function syncStateIcon(state: string) {
  switch (state) {
    case FileSyncState.Pending:
      return <Clock className="size-3.5 text-muted-foreground" />
    case FileSyncState.Completed:
      return <CheckCircle2 className="size-3.5 text-success" />
    case FileSyncState.Error:
      return <AlertCircle className="size-3.5 text-destructive" />
    default:
      return null
  }
}

function formatProcessedAt(tsMs: number | undefined): string | null {
  if (!tsMs || tsMs === 0) return null
  return new Date(tsMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function webCrawlerErrorDisplay(state: string | undefined): string | null {
  if (!state || state === 'no_more_urls' || state === 'backlog_empty') {
    return null
  }

  switch (state) {
    case 'page_limit':
      return 'The maximum number of pages for the website crawler has been reached (50).'
    case 'seed_url_failed':
      return 'No URLs visited. Please check the starting URL.'
    default:
      return 'An error occurred during the crawling process. Please try again.'
  }
}

function FileTreeWithDataLoader({
  projectId,
  connectorId,
  asyncDataLoader,
  editMode,
  onCancelEdit,
  onSaveComplete,
  onAuthorityChanged
}: {
  projectId?: number
  connectorId: number
  asyncDataLoader: TreeDataLoader<TreeItem>
  editMode: boolean
  onCancelEdit: () => void
  onSaveComplete: () => void
  onAuthorityChanged?: () => void | Promise<void>
}) {
  const router = useRouter()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [treeLoadErrors, setTreeLoadErrors] = useState<Record<string, string>>({})

  const recoverableDataLoader = useMemo<TreeDataLoader<TreeItem>>(() => {
    if (!('getChildrenWithData' in asyncDataLoader)) return asyncDataLoader
    return {
      ...asyncDataLoader,
      getChildrenWithData: async (itemId: string) => {
        try {
          const children = await asyncDataLoader.getChildrenWithData(itemId)
          setTreeLoadErrors((current) => {
            if (!(itemId in current)) return current
            const next = { ...current }
            delete next[itemId]
            return next
          })
          return children
        } catch (err) {
          Sentry.captureException(err, { extra: { connectorId, itemId, projectId } })
          setTreeLoadErrors((current) => ({
            ...current,
            [itemId]: err instanceof Error ? err.message : 'Failed to load folder'
          }))
          return []
        }
      }
    }
  }, [asyncDataLoader, connectorId, projectId])

  const tree = useTree<TreeItem>(getTreeConfig({ asyncDataLoader: recoverableDataLoader }))
  const treeState = tree.getState()
  const isFetching = treeState.loadingItemData.length > 0 || treeState.loadingItemChildrens.length > 0
  const hasItems = tree.getItems().length > 0
  const [treeLoadStarted, setTreeLoadStarted] = useState(false)
  const [treeLoadComplete, setTreeLoadComplete] = useState(false)
  if (!treeLoadComplete) {
    if (isFetching && !treeLoadStarted) {
      setTreeLoadStarted(true)
    } else if (!isFetching && (treeLoadStarted || hasItems)) {
      setTreeLoadComplete(true)
    }
  }
  const treeLoading = editMode && !treeLoadComplete

  async function handleSave() {
    try {
      setError(null)
      setSubmitting(true)
      const checkedState = getTreeStateCheckedAndIndeterminateItems(tree)
      const checkedItems: Array<TreeItem> = Object.values(checkedState)
      await saveConnectorConfig(connectorId, projectId, checkedItems)
      if (onAuthorityChanged !== undefined) await onAuthorityChanged()
      else await router.invalidate()
      onSaveComplete()
      onCancelEdit()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId, projectId } })
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  if (treeLoading) {
    return <PanelLoading />
  }

  return (
    <>
      <div
        {...tree.getContainerProps()}
        className={cn(
          'min-h-0 flex-1 overflow-y-auto rounded-md border bg-muted/30 p-3',
          submitting && 'pointer-events-none opacity-50'
        )}
      >
        {tree.getItems().length === 0 && !editMode ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No items included, please configure the connector.
          </div>
        ) : (
          tree.getItems().map((item) => {
            const itemData = item.getItemData()
            const isLoading = item.isLoading()
            const treeLoadError = treeLoadErrors[item.getId()]
            const syncIcon = !isLoading ? syncStateIcon(itemData.syncState) : null
            const detailsDisplay = !isLoading ? processingDetailsDisplay(itemData.processingDetails) : null
            const processedAt = !isLoading ? formatProcessedAt(itemData.lastProcessedAtTsMs) : null
            return (
              <div key={item.getId()}>
                <div className="flex items-center justify-between gap-2 py-0.5">
                  <span className="flex min-w-0 items-center gap-2">
                    <button
                      {...item.getProps()}
                      disabled={isLoading || submitting}
                      style={{ paddingLeft: `${item.getItemMeta().level * 40}px` }}
                      className="flex-1 text-left"
                    >
                      {item.isFolder() ? (
                        <ChevronDown
                          className={cn(
                            'size-4 text-muted-foreground transition-transform',
                            !item.isExpanded() && '-rotate-90',
                            isLoading && 'opacity-50'
                          )}
                        />
                      ) : (
                        <span className="size-4" />
                      )}
                    </button>

                    {editMode && (
                      <input
                        type="checkbox"
                        {...item.getCheckboxProps()}
                        disabled={submitting}
                        className="h-4 w-4 accent-primary"
                      />
                    )}
                    {item.isFolder() ? (
                      item.isExpanded() ? (
                        <FolderOpen className="pointer-events-none size-4 text-muted-foreground" />
                      ) : (
                        <FolderIcon className="pointer-events-none size-4 text-muted-foreground" />
                      )
                    ) : (
                      <FileIcon className="pointer-events-none size-4 text-muted-foreground" />
                    )}
                    <div className="flex items-center gap-1">
                      <span className={cn('rounded px-2 py-0.5 text-sm font-light', item.isFolder() && 'font-medium')}>
                        {item.getItemName()}
                        {item.isLoading() && ' (loading...)'}
                      </span>
                      {syncIcon && <span className="shrink-0">{syncIcon}</span>}
                      {detailsDisplay && (
                        <span className={cn('shrink-0 text-[10px]', detailsDisplay.colourClass)}>
                          {detailsDisplay.label}
                        </span>
                      )}
                      {processedAt && <span className="shrink-0 text-[10px] text-muted-foreground">{processedAt}</span>}
                    </div>
                  </span>
                </div>
                {treeLoadError && (
                  <div
                    className="flex items-center gap-2 py-1 text-sm text-destructive"
                    style={{ paddingLeft: `${(item.getItemMeta().level + 1) * 40}px` }}
                  >
                    <span role="alert">{treeLoadError}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isLoading || submitting}
                      onClick={() => void item.invalidateChildrenIds()}
                    >
                      Retry loading {item.getItemName()}
                    </Button>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
      {editMode && (
        <>
          <div className="mt-1 rounded-md border border-border bg-accent-strong px-3 py-2 text-sm text-foreground">
            We only show supported file types under 25 MB. If a file you want is missing, please let us know at:{' '}
            <a href="mailto:contact@snowvault.com" className="underline">
              contact@snowvault.com
            </a>
          </div>
          {error && (
            <p role="alert" className="mt-1 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="mt-1 flex justify-end gap-2">
            <Button onClick={onCancelEdit} disabled={submitting} variant="outline" size="sm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={submitting || treeLoading} size="sm">
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </>
      )}
    </>
  )
}

type UrlTreeNode = {
  segment: string
  children: Map<string, UrlTreeNode>
}

function buildUrlTree(rootUrl: string, urls: Array<string>): UrlTreeNode {
  const root: UrlTreeNode = { segment: '', children: new Map() }
  let rootPath = ''
  try {
    rootPath = new URL(rootUrl).pathname.replace(/\/$/, '')
  } catch {}

  for (const url of urls) {
    let path: string
    try {
      const parsed = new URL(url)
      path = parsed.pathname
      if (parsed.search) path += parsed.search
      if (parsed.hash) path += parsed.hash
    } catch {
      path = url
    }
    // Strip the root path prefix so only sub-paths remain
    if (rootPath && (path === rootPath || path.startsWith(rootPath + '/'))) {
      path = path.slice(rootPath.length)
    }
    const segments = path.split('/').filter(Boolean)
    let current = root
    for (const segment of segments) {
      if (!current.children.has(segment)) {
        current.children.set(segment, { segment, children: new Map() })
      }
      current = current.children.get(segment)!
    }
  }
  return root
}

const MAX_TREE_DEPTH = 20

function UrlTreeNodes({ node, depth }: { node: UrlTreeNode; depth: number }) {
  if (depth > MAX_TREE_DEPTH) return null
  const children = Array.from(node.children.values()).sort((a, b) => a.segment.localeCompare(b.segment))
  return (
    <>
      {children.map((child) => (
        <div key={child.segment}>
          <div style={{ paddingLeft: `${depth * 16}px` }} className="text-sm">
            /{child.segment}
          </div>
          <UrlTreeNodes node={child} depth={depth + 1} />
        </div>
      ))}
    </>
  )
}

function WebCrawlerSubUrlsTree({ rootUrl, subUrls }: { rootUrl: string; subUrls: Array<string> }) {
  const tree = useMemo(() => buildUrlTree(rootUrl, subUrls), [rootUrl, subUrls])

  return (
    <div className="mt-3 flex min-h-0 flex-1 flex-col">
      <div className="mb-1 text-sm text-muted-foreground">Crawled URLs ({subUrls.length}):</div>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border bg-background p-2">
        <UrlTreeNodes node={tree} depth={1} />
      </div>
    </div>
  )
}

function WebCrawlerConfig({
  currentConfig,
  connectorId,
  projectId,
  editMode,
  onCancelEdit,
  onSaveComplete,
  onAuthorityChanged,
  subUrls
}: {
  currentConfig: WebCrawlerItem
  connectorId: number
  projectId?: number
  editMode: boolean
  onCancelEdit: () => void
  onSaveComplete: () => void
  onAuthorityChanged?: () => void | Promise<void>
  subUrls: Array<string>
}) {
  const router = useRouter()
  const defaultUrl = currentConfig.url || 'https://'
  const [webCrawlerUrl, setWebCrawlerUrl] = useState(defaultUrl)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleSave() {
    if (webCrawlerUrl === '') {
      setError('URL cannot be empty')
      return
    }
    if (!URL.canParse(webCrawlerUrl)) {
      setError('Please enter a valid URL')
      return
    }
    try {
      setError(null)
      setSubmitting(true)
      await saveConnectorConfig(connectorId, projectId, undefined, { url: webCrawlerUrl })
      if (onAuthorityChanged !== undefined) await onAuthorityChanged()
      else await router.invalidate()
      onSaveComplete()
      onCancelEdit()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId, projectId } })
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  if (!editMode) {
    return (
      <div className="flex min-h-0 flex-1 flex-col rounded-md border bg-muted/30 p-3">
        <div className="text-sm">URL: {currentConfig.url}</div>
        <div>{webCrawlerErrorDisplay(currentConfig.state)}</div>
        {subUrls.length > 0 && <WebCrawlerSubUrlsTree rootUrl={currentConfig.url} subUrls={subUrls} />}
      </div>
    )
  }

  return (
    <>
      <Input
        ref={inputRef}
        value={webCrawlerUrl}
        onChange={(e) => setWebCrawlerUrl(e.target.value)}
        onFocus={() => inputRef.current?.select()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void handleSave()
        }}
      />
      {error && (
        <p role="alert" className="mt-1 text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancelEdit}>
          Cancel
        </Button>
        <Button size="sm" disabled={submitting} onClick={handleSave}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </>
  )
}
